import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ModalService } from '@purespectrum1/ui/modal';
import { Subscription, of } from 'rxjs';

import { SupplierAllocationModalComponent } from '../supplier-allocation-modal/supplier-allocation-modal.component';
import { SharedDataService } from '../../../../shared/services/shared-data-service/shared-data-service';
import { SurveyMetaDataService } from '../../../../shared/services/survey-meta-data/survey-meta-data.service';
import { SURVEY_STATUS } from '../../../../shared/constants/survey-status';
import { ConfirmationModalComponent } from '../../../../shared/ui/confirmation-modal/confirmation-modal.component';
import { CreateSurveyService } from '../../../create-survey.service';
import { TRAFFIC_CHANNEL } from '../../../constants/traffic-channels-constants';
import { catchError, delay, filter, map, switchMap } from 'rxjs/operators';
import { AuthService } from '../../.././../shared/services/auth/auth.service';
import { MultiCountryDraftService } from '../../../services/multi-country-draft/multi-country-draft.service';
import { BuyerApiService } from '../../../../shared/services/buyer-api/survey.service';

@Component({
  selector: 'ps-ui-create-survey-data',
  templateUrl: './create-survey-data.component.html',
  styleUrls: ['./create-survey-data.component.css'],
})
export class CreateSurveyDataComponent implements OnInit, OnDestroy {
  public createSurveyHeaderData = false;
  public surveyId: number;
  public surveyFieldTime?: number;
  public surveyLastSaved?: number;
  public surveyCreationObject: any;
  public totalClicks?: number;
  public surveyCompletes?: number;
  public surveyCpi?: number;
  public surveyTotalCost?: number;
  public isMcSurvey: boolean = false;
  public isTCSPAEditMode: boolean = false;
  /** Only display the survey metadata if the survey is new or a draft */
  public isSurveyDraft = false;

  public showTotalCompletes: boolean = false;
  public totalCompletes: number = 0;
  public totalMulticountryCompletes: number = 0;
  public totalCpi: number = 0;
  public averageCpi: number = 0;
  public totalCost: number = 0;
  public currencySymbol: string = '';
  public enableAdvanceFeasibilityDropDown: boolean = false;
  public totalCompleteLabel: string = '';

  private _surveyDataSubscriptions: Subscription[] = [];
  private _isFormDirty: boolean = false;

  @Input() isNewSurvey = false;
  @Input() isLaunchSurvey = false;
  @Input() isRecontactSurvey = false;
  @Input() showCloseButton = true;
  @Output() closeSurvey = new EventEmitter<boolean>();

  private _closeSurveyCommand$ = new EventEmitter();

  constructor(
    private _surveyMetaDataService: SurveyMetaDataService,
    private _sharedDataService: SharedDataService,
    private _modal: ModalService,
    private _createSurveyService: CreateSurveyService,
    private _auth: AuthService,
    private _multiCountryDraftService: MultiCountryDraftService,
    private _buyerSurveyApiService: BuyerApiService
  ) {}

  ngOnInit() {
    this.enableAdvanceFeasibilityDropDown =
      !!this._auth.buyerConfig?.enableAdvanceFeasibilityDropDown;
    this._surveyMetaDataService.clearAll();
    this._surveyDataSubscriptions.push(
      this._sharedDataService.currentSurveySource.subscribe((data) => {
        this.surveyCreationObject = data;
      })
    );

    this._surveyDataSubscriptions.push(
      this._surveyMetaDataService
        .getSurveyHeaderData()
        .subscribe((surveyResponse) => {
          if (surveyResponse) {
            this.surveyId = surveyResponse.surveyId;
            this.surveyFieldTime = surveyResponse.surveyFieldTime;
            this.surveyLastSaved = surveyResponse.surveyLastSaved;
            this.totalClicks = surveyResponse.totalClicks;
            this.surveyCompletes = surveyResponse.completes;
            this.surveyCpi = surveyResponse.cpi;
            this.surveyTotalCost = surveyResponse.total_cost;
            this.isSurveyDraft =
              !surveyResponse.surveyStatus ||
              surveyResponse.surveyStatus === SURVEY_STATUS.DRAFT;
            if (surveyResponse.currencySymbol) {
              this.currencySymbol = surveyResponse.currencySymbol;
            }
          }
        })
    );

    this._surveyDataSubscriptions.push(
      this._surveyMetaDataService
        .getisFormDirty()
        .subscribe((isFormDirty: boolean) => {
          this._isFormDirty = isFormDirty;
        })
    );

    this._surveyDataSubscriptions.push(
      this._surveyMetaDataService.getSurveyData().subscribe((data) => {
        this.isMcSurvey = !!data.mcUniqueId;
        if (data.channel_type === TRAFFIC_CHANNEL.SPECIFIC_AUDIENCE) {
          this.isTCSPAEditMode = true;
        }
      })
    );

    this._surveyDataSubscriptions.push(
      this._createSurveyService
        .getMultiCountryTotalCompletes()
        .pipe(delay(0))
        .subscribe((data) => {
          this.showTotalCompletes = data.showTotalCompletes;
          this.totalCompletes = data.totalCompletes;
          this.totalCompleteLabel = data.totalCompleteLabel;
        })
    );

    this._surveyDataSubscriptions.push(
      this._multiCountryDraftService
        .getMultiCountrySurveyData()
        .pipe(filter((surveys) => !!surveys.length))
        .subscribe((surveys) => {
          this.calculateCostAndCpiForMcSurveys(surveys);
        })
    );

    this._surveyDataSubscriptions.push(
      this._multiCountryDraftService
        .getMcSpvChildSurveys()
        .pipe(filter((childSurveys) => !!childSurveys.length))
        .subscribe((data) => {
          this.calculateCostAndCpiForMcSurveys(data);
        })
    );
    this._handleCloseSurveyScenario();
  }

  calculateCostAndCpiForMcSurveys<
    T extends { cpi: number; completes_required: number }
  >(surveys: T[]) {
    this.totalMulticountryCompletes = 0;
    this.totalCost = 0;
    this.totalCpi = 0;
    surveys.forEach((survey) => {
      this.totalCpi = this.totalCpi + (survey.cpi || 0);
      this.totalMulticountryCompletes =
        this.totalMulticountryCompletes + survey.completes_required;
      this.totalCost =
        this.totalCost + (survey.cpi || 0) * survey.completes_required;
    });
    this.averageCpi = this.totalCpi / surveys.length;
  }

  openSupplierAllocationModal() {
    if (
      this.surveyCreationObject.isSurveyCreated &&
      this.surveyCreationObject.surveyId
    ) {
      this._modal.open(SupplierAllocationModalComponent);
    }
  }

  close() {
    if (!this._isFormDirty) {
      this._closeSurveyCommand$.emit();
      return;
    }

    const exitModalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: 'Are you sure you want to exit, your data will not be saved?\n\nIf you want to save this project, you can use the Save Tool on the bottom left of this screen.',
        cancelButtonLabel: 'No, continue working.',
        confirmButtonLabel: 'Yes, leave page.',
      },
      maxWidth: '35%',
    });

    exitModalRef.onClose$.subscribe((action) => {
      if (action === 'ok') {
        this._closeSurveyCommand$.next();
      }
    });
  }

  private _handleCloseSurveyScenario(): void {
    this._surveyDataSubscriptions.push(
      this._closeSurveyCommand$
        .pipe(
          switchMap(() => {
            if (!this._isFormDirty) {
              return of(true);
            }

            if (!this.isTCSPAEditMode) {
              return of(true);
            }

            if (!this.isSurveyDraft) {
              return of(true);
            }

            return this._buyerSurveyApiService
              .deleteDraftSurvey(this.surveyId)
              .pipe(
                map(() => true),
                catchError((err) => {
                  return of(false);
                })
              );
          })
        )
        .subscribe((value) => {
          // If the survey is to be closed and redirected to dashboard, better we reset the MC data
          if (value) {
            this._multiCountryDraftService.resetMultiCountryData();
          }
          this.closeSurvey.emit(value);
        })
    );
  }

  ngOnDestroy() {
    this._surveyDataSubscriptions.forEach((sub) => sub.unsubscribe());
  }
}
